import { __extends } from "tslib";
import { MethodBase, _Shader_MethodBase } from './MethodBase';
/**
 * EffectEnvMapMethod provides a material method to perform reflection mapping using cube maps.
 */
var EffectEnvMapMethod = /** @class */ (function (_super) {
    __extends(EffectEnvMapMethod, _super);
    /**
     * Creates an EffectEnvMapMethod object.
     * @param envMap The environment map containing the reflected scene.
     * @param alpha The reflectivity of the surface.
     */
    function EffectEnvMapMethod(envMap, alpha) {
        if (alpha === void 0) { alpha = 1; }
        var _this = _super.call(this) || this;
        _this._envMap = envMap;
        _this._alpha = alpha;
        _this.iAddTexture(_this._envMap);
        return _this;
    }
    Object.defineProperty(EffectEnvMapMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return EffectEnvMapMethod.assetType;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EffectEnvMapMethod.prototype, "envMap", {
        /**
         * The cubic environment map containing the reflected scene.
         */
        get: function () {
            return this._envMap;
        },
        set: function (value) {
            if (this._envMap == value)
                return;
            if (this._envMap)
                this.iRemoveTexture(this._envMap);
            this._envMap = value;
            if (this._envMap)
                this.iAddTexture(this._envMap);
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EffectEnvMapMethod.prototype, "mask", {
        /**
         * An optional texture to modulate the reflectivity of the surface.
         */
        get: function () {
            return this._mask;
        },
        set: function (value) {
            if (value == this._mask)
                return;
            if (this._mask)
                this.iRemoveTexture(this._mask);
            this._mask = value;
            if (this._mask)
                this.iAddTexture(this._mask);
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EffectEnvMapMethod.prototype, "alpha", {
        /**
         * The reflectivity of the surface.
         */
        get: function () {
            return this._alpha;
        },
        set: function (value) {
            this._alpha = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @inheritDoc
     */
    EffectEnvMapMethod.prototype.dispose = function () {
    };
    EffectEnvMapMethod.assetType = '[asset EffectEnvMapMethod]';
    return EffectEnvMapMethod;
}(MethodBase));
export { EffectEnvMapMethod };
import { ShaderBase } from '@awayjs/renderer';
/**
 * _Shader_EffectEnvMapMethod provides a material method to perform reflection mapping using cube maps.
 */
var _Shader_EffectEnvMapMethod = /** @class */ (function (_super) {
    __extends(_Shader_EffectEnvMapMethod, _super);
    /**
     * Creates a new _Shader_EffectEnvMapMethod.
     */
    function _Shader_EffectEnvMapMethod(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this._method = method;
        _this._shader = shader;
        return _this;
    }
    /**
     * @inheritDoc
     */
    _Shader_EffectEnvMapMethod.prototype._initVO = function (chunkVO) {
        chunkVO.needsNormals = true;
        chunkVO.needsView = true;
        this._envMap = this._method.envMap.getAbstraction(this._shader);
        this._envMap._initVO(chunkVO);
        if (this._method.mask) {
            this._maskMap = this._method.mask.getAbstraction(this._shader);
            this._shader.uvDependencies++;
        }
        else if (this._maskMap) {
            this._maskMap = null;
        }
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectEnvMapMethod.prototype._initConstants = function () {
        this._envMap._initConstants();
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectEnvMapMethod.prototype.dispose = function () {
        this._envMap = null;
        this._maskMap = null;
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectEnvMapMethod.prototype._activate = function () {
        this._envMap.activate();
        if (this._maskMap)
            this._maskMap.activate();
        if (this._invalid)
            this._updateProperties();
    };
    _Shader_EffectEnvMapMethod.prototype._setRenderState = function (renderState) {
        this._envMap._setRenderState(renderState);
        if (this._maskMap)
            this._maskMap._setRenderState(renderState);
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectEnvMapMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var alphaRegister = registerCache.getFreeFragmentConstant();
        var code = '';
        this._fragmentIndex = alphaRegister.index * 4;
        var temp = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(temp, 1);
        var temp2 = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(temp2, 1);
        // r = I - 2(I.N)*N
        code += 'dp3 ' + temp + '.w, ' + sharedRegisters.viewDirFragment + '.xyz, ' + sharedRegisters.normalFragment + '.xyz\n' +
            'add ' + temp + '.w, ' + temp + '.w, ' + temp + '.w\n' +
            'mul ' + temp + '.xyz, ' + sharedRegisters.normalFragment + '.xyz, ' + temp + '.w\n' +
            'sub ' + temp + '.xyz, ' + temp + '.xyz, ' + sharedRegisters.viewDirFragment + '.xyz\n' +
            this._envMap._getFragmentCode(temp, registerCache, sharedRegisters, temp) +
            'sub ' + temp2 + '.w, ' + temp + '.w, fc0.x\n' + // -.5
            'kil ' + temp2 + '.w\n' + // used for real time reflection mapping - if alpha is not 1 (mock texture) kil output
            'sub ' + temp + ', ' + temp + ', ' + targetReg + '\n';
        if (this._maskMap) {
            code += this._maskMap._getFragmentCode(temp2, registerCache, sharedRegisters, sharedRegisters.uvVarying) +
                'mul ' + temp + ', ' + temp2 + ', ' + temp + '\n';
        }
        code += 'mul ' + temp + ', ' + temp + ', ' + alphaRegister + '.x\n' +
            'add ' + targetReg + ', ' + targetReg + ', ' + temp + '\n';
        registerCache.removeFragmentTempUsage(temp);
        registerCache.removeFragmentTempUsage(temp2);
        return code;
    };
    _Shader_EffectEnvMapMethod.prototype._updateProperties = function () {
        this._shader.fragmentConstantData[this._fragmentIndex] = this._method.alpha;
    };
    return _Shader_EffectEnvMapMethod;
}(_Shader_MethodBase));
export { _Shader_EffectEnvMapMethod };
ShaderBase.registerAbstraction(_Shader_EffectEnvMapMethod, EffectEnvMapMethod);
